import clsx from 'clsx'
import { styled } from '@mui/material'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  Section,
  RouterPanel,
  AnimatedSwitch,
  useSectionHistory,
  useActiveSectionsHistory,
} from 'weblite-router'
import { useCommSectionVisibilityStateInDesktopSize } from '$scenes/components/app/react-hooks'
import { setDesktopApi } from './app.desktop.api'
import {
  CallPanel,
  CourseVitrinePanel,
  HomePanel,
  RoomPanel,
  WappPanel,
  RoomProfilePanel,
  PvCallManagerPanel,
  ProfilePanel,
} from '$scenes/router-panels'
import { useIsFullscreenEnabled } from '$logic/fullscreen/fullscreen-mode'
import { isDesktopSize } from '$func/environmentInfo'

const SectionsContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
`

const SectionContainer = styled('div')`
  min-width: 360px;
  width: 360px;
  overflow: hidden;

  &.grow {
    width: unset;
    flex-grow: 1;
  }

  &.collapsed {
    min-width: 0;
    width: 0;
  }

  &.fullscreen {
    display: block;
    width: 100%;
    height: 100%;

    position: absolute;
    left: 0;
    top: 0;

    z-index: 10;
  }
`

const CommSection = styled('div')`
  position: absolute;
  right: 0;
  top: 0;
  width: 0;
  height: 0;
  display: none;
  z-index: 10;

  &[data-show='true'] {
    width: 360px;
    height: 100%;
    display: block;

    &.fullscreen {
      width: 100%;
      left: 0;
      z-index: 13;
    }
  }
`

export function AppDesktop() {
  const rightSectionHistory = useSectionHistory('right')

  const activeSections = useActiveSectionsHistory()

  const leftPartTopSection = activeSections.reduce(
    (acc, section) =>
      ['left', 'courseVitrine'].includes(section) ? section : acc,
    '',
  )

  const leftPartActiveSectionIndex =
    leftPartTopSection === 'courseVitrine' ? 1 : 0

  // TODO: Extract fullscreen logic into separate hook
  // TODO: Store wappIsFullscreen in WappPanel state. then use getTopWappPanelMeta to get isFullscreen
  const [wappPanelIsFullscreen, setWappPanelIsFullscreen] =
    useState<boolean>(false)

  const wappPanelIsFullscreenRef = useRef<boolean>(wappPanelIsFullscreen)
  useLayoutEffect(() => {
    wappPanelIsFullscreenRef.current = wappPanelIsFullscreen
  }, [wappPanelIsFullscreen])

  useLayoutEffect(() => {
    setDesktopApi({
      getWappPanelIsFullscreen() {
        return wappPanelIsFullscreenRef.current
      },

      setWappPanelIsFullscreen(isFullscreen: boolean) {
        if (isDesktopSize()) setWappPanelIsFullscreen(isFullscreen)
      },
    })
  }, [])

  const { show: showCommSection, fullscreen: commSectionIsFullScreen } =
    useCommSectionVisibilityStateInDesktopSize()

  const midSectionIsFullscreen = useIsFullscreenEnabled()

  return (
    <>
      <SectionsContainer>
        <SectionContainer
          sx={{
            borderLeft: '1px solid',
            borderColor: 'neutral300',
            transform: 'translateZ(0)',
          }}
        >
          <AnimatedSwitch
            keepAllMounted
            name="none"
            activeKey={leftPartActiveSectionIndex}
          >
            <div>
              <Section
                isInitialSection
                name="left"
                transition={{ name: 'none' }}
                initialPanel="home"
              >
                <ProfilePanel.Portal data-name="profile">
                  <RouterPanel />
                </ProfilePanel.Portal>

                <HomePanel.Portal data-name="home">
                  <RouterPanel />
                </HomePanel.Portal>

                <RoomProfilePanel.Portal data-name="roomProfile">
                  <RouterPanel />
                </RoomProfilePanel.Portal>
              </Section>
            </div>

            <div>
              <Section name="courseVitrine" initialPanel="courseVitrine">
                <CourseVitrinePanel.Portal data-name="courseVitrine">
                  <RouterPanel />
                </CourseVitrinePanel.Portal>
              </Section>
            </div>
          </AnimatedSwitch>
        </SectionContainer>

        <SectionContainer
          className="grow"
          sx={{ zIndex: midSectionIsFullscreen ? 11 : undefined }}
        >
          <Section name="mid" initialPanel="room">
            <RoomPanel.Portal data-name="room">
              <RouterPanel />
            </RoomPanel.Portal>
          </Section>
        </SectionContainer>

        <SectionContainer
          className={clsx({
            collapsed: !rightSectionHistory?.length,
            fullscreen: rightSectionHistory?.length && wappPanelIsFullscreen,
          })}
          sx={{ borderRight: '1px solid', borderColor: 'neutral300' }}
        >
          <Section name="right">
            <WappPanel.Portal data-name="wapp">
              <RouterPanel />
            </WappPanel.Portal>
          </Section>
        </SectionContainer>
      </SectionsContainer>

      <CommSection
        data-show={showCommSection}
        className={commSectionIsFullScreen ? 'fullscreen' : ''}
      >
        <Section name="comm" initialPanel="call" transition={{ name: 'none' }}>
          <CallPanel.Portal data-name="call">
            <RouterPanel />
          </CallPanel.Portal>

          <PvCallManagerPanel.Portal data-name="pvCallManager">
            <RouterPanel />
          </PvCallManagerPanel.Portal>
        </Section>
      </CommSection>
    </>
  )
}
